import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Bot,
  LayoutDashboard,
  Wrench,
  Users,
  ArrowRightLeft,
  BarChart2,
  PieChart,
  LogOut,
  X,
  ChevronRight,
  UserCog,
  Shield,
} from 'lucide-react';

const ALL_NAV_ITEMS = [
  { to: '/dashboard',    icon: LayoutDashboard, label: 'Dashboard',    roles: ['admin'] },
  { to: '/tools',        icon: Wrench,          label: 'AI Tools',     roles: ['admin'] },
  { to: '/employees',    icon: Users,           label: 'Employees',    roles: ['admin', 'hr'] },
  { to: '/migrations',   icon: ArrowRightLeft,  label: 'Migrations',   roles: ['admin'] },
  { to: '/reports/cost', icon: BarChart2,       label: 'Cost Report',  roles: ['admin', 'finance'] },
  { to: '/reports/usage',icon: PieChart,        label: 'Usage Report', roles: ['admin'] },
  { to: '/users',        icon: UserCog,         label: 'User Management', roles: ['admin'] },
];

const ROLE_BADGE = {
  admin:   { label: 'Admin',   cls: 'bg-primary-700 text-primary-200' },
  hr:      { label: 'HR',      cls: 'bg-purple-700 text-purple-200' },
  finance: { label: 'Finance', cls: 'bg-emerald-700 text-emerald-200' },
};

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { admin, role, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = ALL_NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));
  const badge = ROLE_BADGE[role] || ROLE_BADGE.admin;

  return (
    <>
      {/* Mobile overlay */}
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-30 bg-primary-900 text-white flex flex-col transition-all duration-300
          ${collapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-primary-800">
          <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary-700" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="font-semibold text-sm leading-tight truncate">AI Tool Management</p>
              <p className="text-primary-300 text-xs truncate">
                {role === 'hr' ? 'HR Portal' : role === 'finance' ? 'Finance Portal' : 'Admin Portal'}
              </p>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto text-primary-300 hover:text-white flex-shrink-0"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 mx-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-700 text-white'
                    : 'text-primary-300 hover:bg-primary-800 hover:text-white'
                }`
              }
              title={collapsed ? label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer / User info */}
        <div className="border-t border-primary-800 p-4">
          {!collapsed && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-3.5 h-3.5 text-primary-400" />
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
              </div>
              <p className="text-sm font-medium text-white truncate">{admin?.name || 'User'}</p>
              <p className="text-xs text-primary-400 truncate">{admin?.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-primary-300 hover:text-red-400 text-sm w-full transition-colors"
            title={collapsed ? 'Logout' : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
